import {
  Button,
  HStack,
  Heading,
  IconButton,
  Input,
  Select,
  Text,
  VStack,
  chakra,
  Checkbox,
  CheckboxGroup,
  Stack,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useFieldArray, useForm, Controller } from "react-hook-form";
import { FaTrashAlt } from "react-icons/fa";
import * as y from "yup";
import { KYDAdminEvent, KYDReportDay } from "@common/interfaces/KYDEvent";
import { useState } from "react";
import { AddIcon } from "@chakra-ui/icons";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";

const DEFAULT_RECIPIENT = { email: "" };
const DAYS_OF_WEEK = [
  { label: "Monday", value: "MON" },
  { label: "Tuesday", value: "TUE" },
  { label: "Wednesday", value: "WED" },
  { label: "Thursday", value: "THU" },
  { label: "Friday", value: "FRI" },
  { label: "Saturday", value: "SAT" },
  { label: "Sunday", value: "SUN" },
];

export enum ReportMode {
  DAILY = "daily",
  WEEKLY = "weekly",
  CUSTOM = "custom",
}

const modes = Object.values(ReportMode);

const emailSchema = y.string().email().required();
const modeSchema = y
  .mixed<ReportMode>()
  .oneOf(Object.values(ReportMode))
  .required();

const recipientSchema = y.object({ email: emailSchema });

const schema = y.object({
  recipients: y.array(recipientSchema).required(),
  mode: modeSchema.required(),
  custom_days: y.array(y.string()).when("mode", {
    is: (mode: ReportMode) => mode === ReportMode.CUSTOM,
    then: (schema) => schema.min(1, "Select at least one day").required(),
    otherwise: (schema) => schema.optional(),
  }),
  report_time: y.string().when("mode", {
    is: (mode: ReportMode) => mode === ReportMode.CUSTOM,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
});

export type RecipientsFormValues = y.InferType<typeof schema>;

const transform = {
  input: (kydEvent: KYDAdminEvent) => {
    if (!kydEvent) {
      return {
        recipients: [],
        mode: ReportMode.DAILY,
        custom_days: [],
        report_time: "",
      };
    }

    return {
      mode: kydEvent.report_recipients?.mode || ReportMode.DAILY,
      recipients:
        kydEvent.report_recipients?.recipients.map((r) => ({
          email: r,
        })) || [],
      custom_days: kydEvent.report_recipients?.custom_days || [],
      report_time: kydEvent.report_recipients?.report_time || "",
    };
  },
  output: (values: RecipientsFormValues) => {
    return {
      mode: values.mode,
      recipients: values.recipients.map((r) => r.email),
      custom_days: values.custom_days as KYDReportDay[],
      report_time: values.report_time,
    };
  },
};

const ReportsForm = ({
  kydEvent,
  onSubmit,
}: {
  kydEvent?: KYDAdminEvent;
  onSubmit: (values: {
    mode: string;
    recipients: string[];
    custom_days?: KYDReportDay[];
    report_time: string;
  }) => Promise<void>;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty, isValid, errors },
  } = useForm<RecipientsFormValues>({
    resolver: yupResolver(schema),
    criteriaMode: "all",
    mode: "onChange",
    reValidateMode: "onChange",
    values: transform.input(kydEvent),
  });

  const { append, fields, remove } = useFieldArray({
    control,
    name: "recipients",
  });

  const _onSubmit = async (values: RecipientsFormValues) => {
    setIsLoading(true);
    await onSubmit(transform.output(values));
    setIsLoading(false);
  };

  const mode = watch("mode");
  const report_time = watch("report_time");
  const custom_days = watch("custom_days");

  return (
    <VStack w={"full"} my={4} alignItems={"flex-start"} gap={6}>
      <HStack
        w="full"
        justifyContent={"space-between"}
        alignItems={"flex-start"}
      >
        <Heading w="full" size="sm" py={2}>
          Ticket Report Emails
        </Heading>
      </HStack>
      <HStack w="full" justifyContent={["space-between", "initial"]} gap={4}>
        <Text fontWeight={"medium"}>Frequency</Text>
        <VStack>
          <Select {...register(`mode`)} isDisabled={isLoading}>
            {modes
              .filter(
                (m) =>
                  m !== ReportMode.WEEKLY ||
                  (m === ReportMode.WEEKLY &&
                    kydEvent?.report_recipients?.mode === ReportMode.WEEKLY)
              )
              .map((mode) => (
                <option
                  disabled={mode === ReportMode.WEEKLY}
                  key={mode}
                  value={mode}
                >
                  {mode}
                </option>
              ))}
          </Select>
        </VStack>
      </HStack>

      {mode === ReportMode.CUSTOM && (
        <VStack w="full" alignItems="flex-start" spacing={2}>
          <Text fontWeight={"medium"}>Select days to send reports:</Text>
          <Controller
            control={control}
            defaultValue={custom_days}
            name="custom_days"
            render={({ field }) => (
              <CheckboxGroup
                value={field.value}
                onChange={(values) => field.onChange(values)}
                isDisabled={isLoading}
              >
                <HStack flexWrap="wrap" spacing={4}>
                  {DAYS_OF_WEEK.map((day) => (
                    <Checkbox
                      key={day.value}
                      value={day.value}
                      sx={{
                        ".chakra-checkbox__control": {
                          borderColor: "black",
                          _checked: {
                            bg: "black",
                            borderColor: "black",
                          },
                        },
                      }}
                    >
                      {day.label}
                    </Checkbox>
                  ))}
                </HStack>
              </CheckboxGroup>
            )}
          />
          {errors.custom_days && (
            <Text color="red.500" fontSize="sm">
              {errors.custom_days.message}
            </Text>
          )}
        </VStack>
      )}

      <VStack w="full" alignItems="flex-start" spacing={0}>
        <Text fontWeight={"medium"} marginBottom={2}>
          Select time to send reports:
        </Text>
        <Controller
          control={control}
          defaultValue={report_time || "12:00"}
          name="report_time"
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label="Report Time"
                value={
                  field.value
                    ? dayjs(field.value, "HH:mm")
                    : dayjs("08:00", "HH:mm")
                }
                onChange={(newValue) => {
                  field.onChange(newValue ? newValue.format("HH:mm") : "12:00");
                }}
                disabled={isLoading}
                views={["hours", "minutes"]}
                ampm={true}
              />
            </LocalizationProvider>
          )}
        />
        {errors.report_time && (
          <Text color="red.500" fontSize="sm">
            {errors.report_time.message}
          </Text>
        )}
      </VStack>
      <Stack w="full" spacing={0}>
        <HStack w="full">
          <Text fontWeight={"medium"} w="full">
            Send reports to:
          </Text>
        </HStack>
        <Stack w="full" spacing={2}>
          {fields.map((field, index) => (
            <HStack w="full" justifyContent={"space-between"} key={field.id}>
              <VStack w="full">
                <Input
                  isDisabled={isLoading}
                  isInvalid={!!errors[`recipients`]?.at(index)?.email}
                  key={field.id}
                  {...register(`recipients.${index}.email`)}
                  placeholder="email@org.com"
                />
              </VStack>
              <VStack justifyContent={"flex-end"} height={"100%"}>
                <IconButton
                  isDisabled={isLoading}
                  w="9%"
                  aria-label={`Delete recipient`}
                  variant={"ghost"}
                  onClick={() => remove(index)}
                  icon={<FaTrashAlt fontSize={"1.0rem"} />}
                />
              </VStack>
            </HStack>
          ))}
        </Stack>
      </Stack>

      <Button
        leftIcon={<AddIcon />}
        size="sm"
        onClick={() => {
          append(DEFAULT_RECIPIENT);
        }}
      >
        Add Recipient
      </Button>
      {isDirty ? (
        <HStack>
          <Button
            isDisabled={!isDirty || !isValid || isLoading}
            isLoading={isLoading}
            color="white"
            bg="green.500"
            mt={2}
            onClick={() => {
              handleSubmit(_onSubmit)();
            }}
          >
            Save
          </Button>

          <Button
            isLoading={isLoading}
            variant="ghost"
            mt={2}
            onClick={() => {
              reset(transform.input(kydEvent));
            }}
          >
            Cancel
          </Button>
        </HStack>
      ) : null}
    </VStack>
  );
};

export default ReportsForm;
